import React from "react";
import { DataTable, DataTableRowReorderEvent } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { InputSwitch } from "primereact/inputswitch";
import toast from "react-hot-toast";

import {
  useGetBenefitFeaturedQuery,
  useReorderBenefitFeaturedMutation,
  useToggleBenefitFeaturedActiveMutation,
  useDeleteBenefitFeaturedMutation,
} from "../../store/services/benefits";
import { BenefitFeatured } from "../../models/benefits";
import { AppDialog, FormSection, RowActions, rowActionsTableStyle } from "../../components/ui";
import { getAdminFacingApiErrorMessage } from "../../utils/functions/api-errors";
import { AddFeaturedBenefitDialog } from "./AddFeaturedBenefitDialog";

interface BenefitFeaturedBenefitsSectionProps {
  /** Cantidad máxima de beneficios destacados activos, según la configuración guardada. */
  maxCount: number;
}

export const BenefitFeaturedBenefitsSection = ({
  maxCount,
}: BenefitFeaturedBenefitsSectionProps) => {
  const { data, isLoading, isFetching, refetch } = useGetBenefitFeaturedQuery();
  const [reorder] = useReorderBenefitFeaturedMutation();
  const [toggleActive] = useToggleBenefitFeaturedActiveMutation();
  const [deleteFeaturedBenefit, { isLoading: isDeleting }] =
    useDeleteBenefitFeaturedMutation();

  const items = React.useMemo(() => data?.items ?? [], [data]);

  const sortedItems = React.useMemo(
    () => [...items].sort((a, b) => a.sortOrder - b.sortOrder),
    [items]
  );

  // Copia local para feedback instantáneo del drag & drop; se resincroniza con
  // el server salvo mientras se está persistiendo un reordenamiento.
  const [isSavingOrder, setIsSavingOrder] = React.useState(false);
  const [rows, setRows] = React.useState<BenefitFeatured[]>([]);
  const [itemToDelete, setItemToDelete] = React.useState<BenefitFeatured>();
  const [isAddDialogVisible, setIsAddDialogVisible] = React.useState(false);

  React.useEffect(() => {
    if (!isSavingOrder) setRows(sortedItems);
  }, [sortedItems, isSavingOrder]);

  const activeCount = items.filter((item) => item.active).length;
  const canAddMore = activeCount < maxCount;

  const hideDeleteDialog = () => setItemToDelete(undefined);

  const handleRowReorder = (e: DataTableRowReorderEvent<BenefitFeatured[]>) => {
    setRows(e.value);
    setIsSavingOrder(true);
    reorder(e.value.map((item) => item.id))
      .unwrap()
      .then(() => toast.success("Orden actualizado"))
      .catch((err) => {
        toast.error(getAdminFacingApiErrorMessage(err, "No se pudo actualizar el orden"));
        void refetch();
      })
      .finally(() => setIsSavingOrder(false));
  };

  const handleToggleActive = (item: BenefitFeatured, active: boolean) => {
    toggleActive({ id: item.id, active })
      .unwrap()
      .catch((err) => {
        toast.error(getAdminFacingApiErrorMessage(err, "No se pudo actualizar el beneficio"));
      });
  };

  const handleDelete = (item: BenefitFeatured) => {
    deleteFeaturedBenefit(item.id)
      .unwrap()
      .then(() => toast.success("Beneficio quitado de destacados"))
      .catch((err) => {
        toast.error(getAdminFacingApiErrorMessage(err, "No se pudo quitar el beneficio"));
      })
      .finally(hideDeleteDialog);
  };

  const nameBodyTemplate = (item: BenefitFeatured) => item.benefitTitle ?? "-";

  const partnerBodyTemplate = (item: BenefitFeatured) => item.benefitPartnerName ?? "-";

  const activeBodyTemplate = (item: BenefitFeatured) => (
    <InputSwitch
      checked={item.active}
      onChange={(e) => handleToggleActive(item, Boolean(e.value))}
      disabled={!item.active && !canAddMore}
    />
  );

  const actionsBodyTemplate = (item: BenefitFeatured) => (
    <RowActions
      actions={[
        { icon: "pi pi-trash", label: "Quitar", severity: "danger", onClick: () => setItemToDelete(item) },
      ]}
    />
  );

  return (
    <FormSection
      title="Beneficios destacados"
      defaultOpen
      titleAction={
        // §6.1 — el alta es un `+` circular; el motivo del bloqueo va en el tooltip.
        <Button
          type="button"
          icon="pi pi-plus"
          rounded
          size="small"
          aria-label="Agregar beneficio"
          onClick={() => setIsAddDialogVisible(true)}
          disabled={!canAddMore}
          tooltip={
            !canAddMore
              ? `Alcanzaste el máximo de ${maxCount} beneficios destacados activos`
              : "Agregar beneficio"
          }
          tooltipOptions={{ position: "top", showOnDisabled: true }}
        />
      }
    >

      <DataTable
        style={rowActionsTableStyle("4.5rem", "4.5rem")}
        value={rows}
        dataKey="id"
        stripedRows
        className="equal-columns-table"
        loading={isLoading || isFetching || isDeleting || isSavingOrder}
        emptyMessage="No hay beneficios destacados todavía"
        reorderableRows={!isSavingOrder}
        onRowReorder={handleRowReorder}
      >
        <Column rowReorder style={{ width: "3.5rem", minWidth: "3.5rem" }} />
        <Column header="Beneficio" style={{ width: "33.333%" }} body={nameBodyTemplate} />
        <Column header="Partner" style={{ width: "33.333%" }} body={partnerBodyTemplate} />
        <Column header="Activo" style={{ width: "16.667%" }} body={activeBodyTemplate} alignHeader="center" bodyClassName="text-center" />
        <Column
          header={<span className="row-actions-label">Acciones</span>}
          // Bloque contra el borde derecho con el mismo margen que la primera columna
          // a la izquierda; título e íconos centrados dentro del bloque.
          style={{ width: "16.667%" }}
          headerClassName="row-actions-th"
          bodyClassName="row-actions-cell"
          body={actionsBodyTemplate}
          exportable={false}
        />
      </DataTable>

      <AppDialog
        variant="destructive"
        visible={!!itemToDelete}
        onHide={hideDeleteDialog}
        header="Quitar beneficio destacado"
        primaryLabel="Quitar Beneficio"
        onPrimary={() => itemToDelete && handleDelete(itemToDelete)}
        loading={isDeleting}
      >
        <span>
          ¿Estás seguro de que querés quitar{" "}
          <strong>{itemToDelete?.benefitTitle}</strong> de los beneficios destacados?
        </span>
      </AppDialog>

      <AddFeaturedBenefitDialog
        visible={isAddDialogVisible}
        onHide={() => setIsAddDialogVisible(false)}
        existingBenefitIds={items.map((item) => item.benefitId)}
      />
    </FormSection>
  );
};
